import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useSpace } from "@/hooks/useSpace";
import { useMyProfile } from "@/hooks/useMyProfile";
import { Avatar } from "@/components/account/Avatar";
import { AccountSheet } from "@/components/account/AccountSheet";
import { spaceHomePath } from "@/domain/space";
import { menuKeyOfPath, menuPath } from "@/domain/menus";

/**
 * MobileTabBar — MO(≤659px) 전용 iOS 26 Liquid Glass 플로팅 하단 탭바.
 *
 * 구성: 메뉴(드로어) · 출하조회 · Home(중앙) · 판매관리 · 프로필(아바타 → 계정 시트, 12 설계 §8-A A-3).
 * 좌우 라우트 탭(출하조회·판매관리)은 게이트 권한 보유 시에만 노출.
 *
 * 디자인:
 *  - 모서리 둥근 사각형(border-radius 30px), 화면 하단에 바짝 붙은 넓은 floating 유리 바.
 *  - 단일 인디케이터 알약(.mtab-pill) 하나가 활성 탭으로 슬라이드(transform+width).
 *  - 스크롤 다운 → 축소(라벨 페이드아웃·아이콘만·scale 0.92), 업 → 확장.
 *  - placePill(animate) 단일 함수가 ①최초 ②resize ③축소/확장 ④클릭/네비게이션 시 위치 재계산.
 *
 * CSS 는 components.css 의 `.mobile-tabbar` (base display:none + MO 미디어쿼리에서 표시).
 */
interface MobileTabBarProps {
  /** 바텀시트 메뉴 열림 여부 (아이콘 햄버거↔X 전환) */
  menuOpen: boolean;
  /** "메뉴" 탭 → 바텀시트 토글 */
  onToggleMenu: () => void;
}

const SPRING = "cubic-bezier(0.34,1.4,0.4,1)";
const DUR = 0.45; // s
const THRESHOLD = 12; // 스크롤 임계값(px) — 손가락 떨림·미세 스크롤에 튀지 않게 넉넉히

export function MobileTabBar({ menuOpen, onToggleMenu }: MobileTabBarProps) {
  const { user } = useAuth();
  const { photo } = useMyProfile();
  const location = useLocation();
  // 계정 시트 — 연 주소를 기억해 두고 주소가 바뀌면(화면 이동) 닫힌 것으로 본다(effect 없이)
  const [acctOpenAt, setAcctOpenAt] = useState<string | null>(null);
  const acctOpen = acctOpenAt === location.pathname;

  // B′: 출하조회·판매관리는 법인 공간 메뉴(중앙·경원 둘 다 있음) → 내 업무 공간에서는 숨김.
  // Home 탭은 지금 공간의 홈(내 업무 = /me, 법인 = /c/{tenant}). 링크·권한은 지금 법인 기준.
  const { space, biz, hasPermIn } = useSpace();
  const inTenant = space !== "my";
  const homePath = spaceHomePath(space);
  const showDispatch = inTenant && (hasPermIn("shipment.view") || hasPermIn("dispatch.view"));
  const showSales = inTenant && hasPermIn("sales.view");

  const navRef = useRef<HTMLElement | null>(null);
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLElement | null>>({});
  const mountedRef = useRef(false);
  const reduceMotion = useRef(
    typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [collapsed, setCollapsed] = useState(false);

  // 활성 탭 결정: 메뉴 열림 우선, 그 외 현재 라우트. 어느 탭에도 안 맞으면 알약 숨김.
  const path = location.pathname;
  let activeKey = "";
  if (menuOpen) activeKey = "menu";
  else if (acctOpen) activeKey = "profile";
  else if (path === homePath) activeKey = "home";
  else if (showDispatch && menuKeyOfPath(path) === "dispatch-view") activeKey = "dispatch";
  else if (showSales && menuKeyOfPath(path) === "sales") activeKey = "sales";

  /** 단일 인디케이터 위치 계산. animate=false → 즉시 스냅, true → 강제 리플로우 후 스프링 슬라이드 */
  const placePill = useCallback(
    (animate: boolean) => {
      const nav = navRef.current;
      const pill = pillRef.current;
      if (!nav || !pill) return;
      const el = activeKey ? tabRefs.current[activeKey] : null;
      if (!el) {
        pill.style.opacity = "0";
        pill.style.width = "0px";
        return;
      }
      // offsetLeft/offsetWidth 는 레이아웃 값이라 바의 transform:scale(축소) 영향을 받지 않음.
      // getBoundingClientRect 를 쓰면 scale 이 좌표에 이중 적용돼 축소 시 인디케이터가 어긋남.
      const x = el.offsetLeft;
      const w = el.offsetWidth;
      if (w === 0) return; // 숨겨진 상태(non-MO) — 계산 스킵

      if (animate && !reduceMotion.current) {
        pill.style.transition = "none";
        void pill.offsetWidth; // 강제 리플로우 → transition:none 직후에도 트랜지션 정상 작동
        // height 도 함께 — 축소/확장 시 바(0.45s)와 알약 높이가 같은 스프링으로 움직이게(툭 끊김 방지)
        pill.style.transition = `transform ${DUR}s ${SPRING}, width ${DUR}s ${SPRING}, height ${DUR}s ${SPRING}`;
      } else {
        pill.style.transition = "none";
      }
      pill.style.opacity = "1";
      pill.style.width = `${w}px`;
      pill.style.transform = `translate(${x}px, -50%)`;
    },
    [activeKey],
  );

  // 활성탭/축소상태/탭구성 변경 시 위치 갱신. 최초 1회는 애니메이션 없이(구석 슬라이드 방지).
  useLayoutEffect(() => {
    placePill(mountedRef.current);
    mountedRef.current = true;
  }, [placePill, collapsed, showDispatch, showSales]);

  // 바 transition 종료 시 픽셀 단위 보정 → 누적 오차 제거 (transform 한 신호만 사용)
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const onEnd = (e: TransitionEvent) => {
      if (e.target === nav && e.propertyName === "transform") placePill(true);
    };
    nav.addEventListener("transitionend", onEnd);
    return () => nav.removeEventListener("transitionend", onEnd);
  }, [placePill]);

  // 스크롤 방향 감지(임계값 4px) → 축소/확장. setState 동일값은 React 가 bail-out → 떨림 없음.
  useEffect(() => {
    const content = document.querySelector(".content");
    if (!content) return;
    let lastY = content.scrollTop;
    const onScroll = () => {
      const y = content.scrollTop;
      // iOS 끝단 바운스(overscroll)는 방향이 왕복해 축소↔확장이 튀므로 범위 밖 값은 무시
      const maxY = content.scrollHeight - content.clientHeight;
      if (y < 0 || y > maxY) return;
      const dy = y - lastY;
      if (Math.abs(dy) < THRESHOLD) return;
      if (y < 8) setCollapsed(false); // 최상단 근처는 항상 펼침
      else if (dy > 0) setCollapsed(true); // 아래로 → 축소
      else setCollapsed(false); // 위로 → 확장
      lastY = y;
    };
    content.addEventListener("scroll", onScroll, { passive: true });
    return () => content.removeEventListener("scroll", onScroll);
  }, []);

  // 창 크기 변경: 즉시 스냅
  useEffect(() => {
    const onResize = () => placePill(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [placePill]);

  // A-3: 아바타 = 계정 시트(옛 동작 "즉시 로그아웃 확인"은 시트 맨 아래 [로그아웃]으로)
  function handleProfile() {
    if (menuOpen) onToggleMenu();
    setAcctOpenAt(acctOpen ? null : location.pathname);
  }

  return (
    <>
    <nav
      ref={navRef}
      className={`mobile-tabbar ${collapsed ? "is-collapsed" : ""}`}
      aria-label="하단 메뉴"
      // 축소 상태의 첫 탭은 "펼치기"만 수행 — 이동·메뉴 열기·로그아웃은 실행하지 않음.
      // capture 단계에서 preventDefault(링크 이동 차단) + stopPropagation(자식 onClick 차단).
      onClickCapture={(e) => {
        if (!collapsed) return;
        e.preventDefault();
        e.stopPropagation();
        setCollapsed(false);
      }}
    >
      <span className="mtab-pill" ref={pillRef} aria-hidden="true" />

      <button
        type="button"
        ref={(el) => {
          tabRefs.current.menu = el;
        }}
        className={`mtab ${menuOpen ? "is-active" : ""}`}
        onClick={onToggleMenu}
        aria-label={menuOpen ? "메뉴 닫기" : "전체 메뉴"}
        aria-expanded={menuOpen}
      >
        <span className="mtab-ico"><Icon name={menuOpen ? "close" : "menu"} size={22} /></span>
        <span className="mtab-label">메뉴</span>
      </button>

      {showDispatch && (
        <NavLink
          to={menuPath("dispatch-view", space, biz)}
          ref={(el) => {
            tabRefs.current.dispatch = el;
          }}
          className={({ isActive }) => `mtab ${isActive ? "is-active" : ""}`}
        >
          <span className="mtab-ico"><Icon name="truck" size={22} /></span>
          <span className="mtab-label">출하조회</span>
        </NavLink>
      )}

      <NavLink
        to={homePath}
        end
        ref={(el) => {
          tabRefs.current.home = el;
        }}
        className={({ isActive }) => `mtab mtab-home ${isActive ? "is-active" : ""}`}
      >
        <span className="mtab-ico"><Icon name="home" size={24} /></span>
        <span className="mtab-label">Home</span>
      </NavLink>

      {showSales && (
        <NavLink
          to={menuPath("sales", space, biz)}
          ref={(el) => {
            tabRefs.current.sales = el;
          }}
          className={({ isActive }) => `mtab ${isActive ? "is-active" : ""}`}
        >
          <span className="mtab-ico"><Icon name="coin" size={22} /></span>
          <span className="mtab-label">판매관리</span>
        </NavLink>
      )}

      <button
        type="button"
        ref={(el) => {
          tabRefs.current.profile = el;
        }}
        className="mtab mtab-avatar"
        onClick={handleProfile}
        aria-label={user ? `${user.name} 계정` : "계정"}
        aria-expanded={acctOpen}
      >
        <span className="mtab-ico"><Avatar className="mtab-av" photo={photo} name={user?.name} loginId={user?.loginId} /></span>
        <span className="mtab-label">{user?.name || "사용자"}</span>
      </button>
    </nav>
    <AccountSheet open={acctOpen} onClose={() => setAcctOpenAt(null)} />
    </>
  );
}
